import {
    CEventData
} from "crqlar-react-common/dist/models/common/calendar/eventData.model";
import {
    GuestCount,
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import React from "react";
import {
    AgeCategory,
    AgeCategoryType
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Service} from "crqlar-react-common/models/service/service.model";
import {
    Availability
} from "crqlar-react-common/dist/models/availability/availability.model";
import {Stack, Typography} from "@mui/material";
import {MbscCalendarEvent, Popup} from "@mobiscroll/react";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {hexToRgba} from "crqlar-react-common/dist/utils/color.util";
import {
    AvailabilityException
} from "crqlar-react-common/dist/models/availability/availabilityException.model";
import {useTranslation} from "react-i18next";

interface Props {
    isTooltipOpen: boolean
    anchor: HTMLElement
    handleTooltipClose: () => void
    event?: MbscCalendarEvent
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    services: Service[]
    ageCategories?: AgeCategory[]
}

const CalendarEventServicesTooltip = (props: Props) => {
    const eventData = props.event?.event as CEventData | undefined
    const availability = eventData?.availability as Availability | undefined
    const exceptions = (eventData?.exceptions ?? []) as AvailabilityException[]
    const maxCapacity: number | undefined = eventData?.maxCapacity ?? undefined
    const service = props.services.find(s => s.id === eventData?.serviceId)
    const reservations = (eventData?.reservations ?? []) as Reservation[]

    const eventStart = eventData?.start as Date
    const eventEnd = eventData?.end as Date

    // Check if this event overlaps with any exception
    const hasException = exceptions.some(exception => {
        if (!exception.originalStartTime || !exception.newEndTime) return false;

        const exceptionStart = new Date(exception.originalStartTime);
        const exceptionEnd = new Date(exception.newEndTime);
        const eventStartTime = new Date(eventStart).getTime();
        const eventEndTime = new Date(eventEnd).getTime();
        const exceptionStartTime = exceptionStart.getTime();
        const exceptionEndTime = exceptionEnd.getTime();

        // Check if times overlap: event overlaps with exception if
        // event start is before exception end AND event end is after exception start
        return eventStartTime < exceptionEndTime && eventEndTime > exceptionStartTime;
    });

    const guestCounts = reservations
        .flatMap(r => r.guestCounts || [])
        .filter(Boolean) as GuestCount[]

    const totalGuestCount = guestCounts.reduce((sum, gc) => sum + (gc.count || 0), 0)

    const adultAgeCategories =
        props.ageCategories?.filter(ac => ac.type === AgeCategoryType.ADULT).map(ac => ac.id) || []
    const nonAdultAgeCategories =
        props.ageCategories?.filter(ac => ac.type !== AgeCategoryType.ADULT).map(ac => ac.id) || []

    const adultGuestCount =
        guestCounts
            .filter(gc => adultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0)

    const nonAdultGuestCount =
        guestCounts
            .filter(gc => nonAdultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0)


    const isDisabled = availability?.availabilityTypeClean === "CLOSED" || hasException;
    const backgroundColor = isDisabled
        ? hexToRgba(service?.color?.background ?? CommonThemeColors.sys.success.background, 0.3)
        : service?.color?.background ?? CommonThemeColors.sys.success.background;

    const textColor = isDisabled
        ? CommonThemeColors.sys.availability.boldRed
        : service?.color?.text || CommonThemeColors.black;

    const {i18n} = useTranslation()
    const displayName =
        service?.nameLocalized?.[i18n.language] ||
        service?.name ||
        availability?.name ||
        "Service";


    const renderServiceName = () => (
        <Typography
            variant="h6"
            sx={{
                padding: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: textColor,
            }}
        >
            {displayName}
        </Typography>
    )

    const renderAvailabilityTime = () => {
        if (!eventData?.start || !eventData?.end) return null
        return (
            <Typography
                variant="h6"
                sx={{
                    padding: 1,
                    whiteSpace: 'nowrap',
                    color: textColor,
                }}
            >
                {`${formatDateHM(eventData.start)} - ${formatDateHM(eventData.end)}`}
            </Typography>
        )
    }

    const renderCapacity = () => (
        <Stack
            direction="row"
            sx={{
                justifyContent: 'space-between',
                gap: '5px',
                display: 'flex',
                alignItems: 'center',
                width: '100%'
            }}
        >
            <Typography
                whiteSpace="nowrap"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flex: 'auto',
                    fontSize: '14px',
                    ml: '-0.35em'
                }}
            >
                {maxCapacity ? `${totalGuestCount}/${maxCapacity}` : `${totalGuestCount}`}
            </Typography>
            <Typography
                whiteSpace="nowrap"
                sx={{
                    textAlign: 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flex: 'auto',
                    fontSize: '14px',
                    mr: '-0.35em'
                }}
            >
                {`${totalGuestCount} (${adultGuestCount}/${nonAdultGuestCount})`}
            </Typography>
        </Stack>
    )

    return (
        <Popup
            contentPadding={false}
            display="anchored"
            anchor={props.anchor}
            isOpen={props.isTooltipOpen}
            scrollLock={false}
            showOverlay={false}
            touchUi={false}
            width="auto"
            onClose={props.handleTooltipClose}
        >
            <Stack
                direction="column"
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{
                        backgroundColor: backgroundColor,
                        backgroundImage: isDisabled
                            ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                ${hexToRgba(textColor, 0.1)} 10px,
                ${hexToRgba(textColor, 0.1)} 20px
              )`
                            : 'none',
                    }}
                >
                    {renderServiceName()}
                    {renderAvailabilityTime()}
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}
                       sx={{p: 2}}>
                    {renderCapacity()}
                </Stack>
            </Stack>
        </Popup>
    )
}

export default CalendarEventServicesTooltip
